import { useEffect, useMemo } from 'react';
import logger from 'ws-scripts/modules/logger';
import { useSitemap, usePrefs } from 'wsm-common-data';
import { BODYSTYLE_FACET_ID } from '../global-constants';
import {
	generateTileData,
	readFacetValues,
	toAcceptableBodystyles
} from '../utils/bodystyleData';
import { useInvData } from './useInvData';

// The sitemap alias for the site's default inventory listing page, declared in
// config/sitemap.json.
const INVENTORY_LISTING_ALIAS = 'INVENTORY_LISTING_DEFAULT_AUTO_ALL';

const LOG_PREFIX = '[ws-bodystyle-selector][useBodystyleData]';

const EMPTY_STATE = { bodystyleData: null, isReady: false, error: null };

// A missing/empty facet or an inventory with no acceptable body styles is a
// legitimate empty state, not a failure: the hook has finished resolving and
// the answer is "no body styles", so it reports ready with an empty list.
const RESOLVED_EMPTY_STATE = {
	...EMPTY_STATE,
	bodystyleData: [],
	isReady: true
};

/**
 * Hook that returns tile data for the acceptable body styles in inventory.
 *
 * Waits on [useInvData] for `window.DDC.InvData`, then reads the
 * `normalBodyStyle` facet out of `InvData.getFacets()`. The parsing lives in
 * utils/bodystyleData: the facet's values are narrowed to the acceptable body
 * styles and turned into the tile data wsm-tiles renders. Tile links point at
 * the inventory listing page the sitemap names
 *
 * `error` is reserved for genuine failures (the InvData wait timing out, or
 * getFacets throwing) that warrant error copy. Legitimate empty states resolve
 * ready with an empty `bodystyleData` instead, leaving `error` null so callers
 * can render nothing.
 *
 * @returns {{ bodystyleData: Array<{ bodystyle: Object }> | null, isReady: boolean, error: string | null }}
 */
export const useBodystyleData = () => {
	const {
		isReady: isInvDataReady,
		invData,
		error: invDataError
	} = useInvData();
	const sitemap = useSitemap();
	const { styleVariant, graphicsColor } = usePrefs();

	// The inventory listing the tiles link to, so the links follow the site's
	// own inventory (new, used or both) rather than assuming new. Null when the
	// site does not publish the alias, which generateTileData falls back for.
	const searchResultPageUrl = sitemap.get(INVENTORY_LISTING_ALIAS);

	// The memo stays a pure derivation; `log` describes what the effect below
	// should report so the logging happens once per result, not per evaluation.
	const { state, log } = useMemo(() => {
		// Surface the hook's own failure rather than a second, vaguer one.
		if (invDataError) {
			return { state: { ...EMPTY_STATE, error: invDataError } };
		}

		if (!isInvDataReady) {
			return { state: EMPTY_STATE };
		}

		// useInvData only reports ready once InvData carries a callable
		// getFacets, so it is safe to call here. The catch below still covers
		// the call itself throwing.
		let values = null;
		try {
			values = readFacetValues(invData.getFacets());
		} catch (caught) {
			const message = `InvData.getFacets threw: ${caught.message}`;
			return {
				state: { ...EMPTY_STATE, error: message },
				log: { level: 'error', message }
			};
		}

		if (!values?.length) {
			const message = `No "${BODYSTYLE_FACET_ID}" facet values in getFacets payload`;
			return {
				state: RESOLVED_EMPTY_STATE,
				log: { level: 'warn', message }
			};
		}

		const bodystyles = toAcceptableBodystyles(values);

		if (!bodystyles.length) {
			const message = `No acceptable body styles among the ${values.length} "${BODYSTYLE_FACET_ID}" facet values`;
			return {
				state: RESOLVED_EMPTY_STATE,
				log: { level: 'warn', message }
			};
		}

		return {
			state: {
				bodystyleData: generateTileData(bodystyles, {
					inventoryPath: searchResultPageUrl,
					styleVariant,
					iconColor: graphicsColor
				}),
				isReady: true,
				error: null
			},
			log: {
				level: 'debug',
				message: `Found ${bodystyles.length} of ${values.length} body styles`
			}
		};
	}, [
		isInvDataReady,
		invData,
		invDataError,
		searchResultPageUrl,
		styleVariant,
		graphicsColor
	]);

	useEffect(() => {
		if (log) {
			logger[log.level](`${LOG_PREFIX} ${log.message}`);
		}
	}, [log]);

	return state;
};

export default useBodystyleData;
